"use client";

import { useCallback, useEffect, useRef, useState } from "react";

type Options = {
  /** Pause before the first step. */
  lead?: number;
  /** Pause between steps. */
  gap?: number;
  /**
   * Extra gate on top of visibility. The pinned deck passes "is this the front
   * card", because all three cards sit in one grid cell and are technically on
   * screen from the moment the section pins — without this, cards 2 and 3
   * would play themselves out while card 1 is still in front. Left unset
   * (the plain vertical list), being on screen is enough.
   */
  active?: boolean;
  /**
   * Run the script over and over instead of once. For a screen whose whole
   * point is the switching itself — the branch picker on card 1 — where a
   * single pass leaves a still image for anyone who looks a moment later.
   */
  loop?: boolean;
};

/**
 * Plays a scripted demo through a mock-up the first time the reader actually
 * arrives at it.
 *
 * The section-3 screens are only obviously interactive once something moves,
 * so each one performs its own headline move — switch branch, retarget a
 * coupon, send an order to the kitchen — instead of waiting to be discovered.
 *
 * Three rules the mock-ups all share, which is why this lives in one place:
 * it plays a single pass unless asked to `loop`, the reader touching any
 * control wins immediately and permanently (`cancel`), and it doesn't run at
 * all under `prefers-reduced-motion`.
 *
 * Attach `ref` to the element whose visibility should arm the demo, and call
 * `cancel` from every control the reader can operate.
 */
export function useSelfDemo<T extends HTMLElement>(
  steps: Array<() => void>,
  { lead = 700, gap = 950, active = true, loop = false }: Options = {},
) {
  const ref = useRef<T>(null);
  const timers = useRef<number[]>([]);
  const cycle = useRef<number>(undefined);
  /** Latches on the first run — and on `cancel`, so neither can be repeated. */
  const spent = useRef(false);
  // Captured from the first render deliberately: every step does nothing but
  // call a state setter, and setters are stable for the life of a component,
  // so there is nothing a later render could change here.
  const script = useRef(steps);
  const [onScreen, setOnScreen] = useState(false);

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    clearInterval(cycle.current);
    cycle.current = undefined;
  }, []);

  /** Reader took over: drop the pending steps and never schedule again. */
  const cancel = useCallback(() => {
    spent.current = true;
    clearTimers();
  }, [clearTimers]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        setOnScreen(true);
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!onScreen || !active || spent.current) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    if (!loop) {
      spent.current = true;
      script.current.forEach((step, i) => {
        timers.current.push(window.setTimeout(step, lead + i * gap));
      });
      return;
    }

    // One interval owns the whole run and hands out the steps in turn, so the
    // cadence can't drift the way a chain of re-armed timeouts does. `spent`
    // stays unlatched here — a loop has no pass to burn — so the run ends only
    // when the reader takes over (`cancel`) or the card stops being active,
    // which the cleanup below covers.
    let step = 0;
    const play = () => {
      script.current[step % script.current.length]();
      step += 1;
    };
    timers.current.push(
      window.setTimeout(() => {
        play();
        cycle.current = window.setInterval(play, gap);
      }, lead),
    );

    return clearTimers;
  }, [onScreen, active, lead, gap, loop, clearTimers]);

  // Drop any pending steps if the card unmounts mid-demo. Deliberately
  // `clearTimers` and not `cancel`: React's development double-invoke mounts,
  // unmounts and remounts every effect, so latching `spent` here would burn
  // the demo before the card was ever on screen.
  useEffect(() => clearTimers, [clearTimers]);

  return { ref, cancel };
}
